import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Loader,
  Select,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { AreaChart, BarsList, LineChart } from "@mantine/charts";
import { DatePickerInput } from "@mantine/dates";
import {
  IconAlertTriangle,
  IconBox,
  IconShoppingCart,
  IconPlugConnected,
  IconChevronLeft,
  IconChevronRight,
  IconCategory,
  IconCircleCheck,
  IconCoin,
  IconLayoutDashboard,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { getProducts } from "@/api/products";
import { getCategories } from "@/api/category";
import { getBranches } from "@/api/branches";
import { getRevenue, syncRevenue } from "@/api/revenue";
import { ProductSales } from "@/pages/ProductSales";
import { notify } from "@/lib/notify";
import { PageTitle } from "@/components/PageTitle";
import { useAccess } from "@/hooks/useAccess";
import { useCooldown } from "@/hooks/useCooldown";
import { ViewTransition } from "@/components/ViewTransition";
import { ListEmpty } from "@/components/DataList";
import {
  branchBarColor,
  branchColor,
  formatPeriodRange,
  formatPrice,
  formatPriceCompact,
  toIsoDay,
  REVENUE_PERIODS,
  REVENUE_PERIOD_LABELS,
} from "@/lib/format";
import { StatCard } from "@/components/StatCard";
import { SyncButton } from "@/components/SyncButton";

// API dönem ızgarasını sonuna kadar sıfırla dolduruyor, henüz gelmemiş günler dahil. Ayın
// ortasında bakıldığında grafik ayın kalanını sıfır çizgisi olarak çiziyor ve bu "ciro çöktü"
// diye okunuyor. Bugünden sonrasında ciro OLAMAZ; o noktaları çizmiyoruz. Geçmişteki gerçek
// sıfırlar duruyor — onlar bilgi.
function countUpToToday(range, grain, total) {
  if (!range?.from) {
    return total;
  }

  const start = new Date(`${range.from}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const elapsed =
    grain === "month"
      ? (today.getFullYear() - start.getFullYear()) * 12 +
        (today.getMonth() - start.getMonth())
      : Math.floor((today - start) / 86_400_000);

  return Math.max(0, Math.min(total, elapsed + 1));
}

const ALL_BRANCHES = "all";
const REVENUE_SYNC_COOLDOWN_SECONDS = 120;

export default function Dashboard() {
  const navigate = useNavigate();
  const { can } = useAccess();

  const [tab, setTab] = useState("revenue");
  const [period, setPeriod] = useState("month");
  const [offset, setOffset] = useState(0);
  const [customRange, setCustomRange] = useState([null, null]);

  const customFrom = toIsoDay(customRange[0]);
  const customTo = toIsoDay(customRange[1]);
  const [branchId, setBranchId] = useState(ALL_BRANCHES);

  const { data: products } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });
  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
  const { data: branches } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranches,
  });

  const queryClient = useQueryClient();
  const { seconds: syncCooldown, start: startSyncCooldown } = useCooldown(
    "revenue-sync-cooldown",
  );

  const syncMutation = useMutation({
    mutationFn: syncRevenue,
    onSuccess: (result) => {
      startSyncCooldown(REVENUE_SYNC_COOLDOWN_SECONDS);
      queryClient.invalidateQueries({ queryKey: ["revenue"] });
      queryClient.invalidateQueries({ queryKey: ["daily-sales"] });
      // Şube ADI yazılıyor: "2 bağlantı yanıt vermedi" hangi şubenin rakamının eski kaldığını
      // söylemiyordu ve ekrandaki sayı güncellenmiş gibi duruyordu. Aynı şubenin iki bağlantısı
      // olabildiği için tekilleniyor (Bostancı'da hem POS hem web sitesi var).
      const offlineNames = [...new Set(result?.offlineBranchIds ?? [])].map(
        (id) => branches?.find((branch) => branch.id === id)?.name ?? `#${id}`,
      );

      if (!offlineNames.length) {
        notify.success("Ciro güncellendi");
        return;
      }

      const message = `${offlineNames.join(", ")} POS'una ulaşılamadı — bu şubelerin cirosu eski kaldı`;

      // Hiçbir şey yazılamadıysa bu bir başarı değil: yeşil bildirim rakamın tazelendiğini
      // söylerdi.
      if (result?.written) {
        notify.warning(message);
      } else {
        notify.error(message);
      }
    },
    onError: (error) => {
      if (error.response?.status === 429) {
        startSyncCooldown(REVENUE_SYNC_COOLDOWN_SECONDS);
      }
      notify.error(error.response?.data?.error ?? "Ciro güncellenemedi");
    },
  });

  const visibleBranches = useMemo(() => {
    if (!branches?.length) {
      return [];
    }
    return branchId === ALL_BRANCHES
      ? branches
      : branches.filter((item) => String(item.id) === branchId);
  }, [branches, branchId]);

  const {
    data: revenue,
    isFetching,
    isError,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["revenue", period, branchId, offset, customFrom, customTo],
    enabled: period !== "custom" || Boolean(customFrom && customTo),
    queryFn: () =>
      getRevenue({
        period,
        offset,
        from: customFrom,
        to: customTo,
        branchId: branchId === ALL_BRANCHES ? null : Number(branchId),
      }),
    placeholderData: (previous) => previous,
  });

  const chartData = useMemo(() => {
    if (!revenue) {
      return [];
    }
    // Veri GELMEMİŞ gün ile cirosu SIFIR olan gün aynı şey değil. `?? 0` ikisini birleştirip
    // ayın kalanını sıfır çizgisi olarak çiziyordu; ay ortasında bakan biri bunu "ciro
    // çöktü" diye okuyor. `null` verince çizgi orada bitiyor, gerçek sıfırlar duruyor.
    const drawable = countUpToToday(
      revenue.range,
      revenue.grain,
      revenue.points.length,
    );

    return revenue.points.map((point, index) => {
      const row = { label: point.label };
      for (const branch of visibleBranches) {
        row[branch.name] =
          index < drawable ? (point.values[branch.id] ?? null) : null;
      }
      return row;
    });
  }, [revenue, visibleBranches]);

  const series = useMemo(
    () =>
      visibleBranches.map((branch, index) => ({
        name: branch.name,
        color: branchColor(index),
      })),
    [visibleBranches],
  );

  const branchTotals = useMemo(() => {
    if (!revenue) {
      return [];
    }
    return visibleBranches
      .map((branch, index) => ({
        name: branch.name,
        value: revenue.points.reduce(
          (sum, point) => sum + (point.values[branch.id] ?? 0),
          0,
        ),
        color: branchBarColor(index),
      }))
      .sort((a, b) => b.value - a.value);
  }, [revenue, visibleBranches]);

  const sparkData = useMemo(
    () =>
      (revenue?.points ?? [])
        .slice(
          0,
          countUpToToday(
            revenue?.range,
            revenue?.grain,
            revenue?.points?.length ?? 0,
          ),
        )
        .map((point) =>
          Object.values(point.values).reduce((sum, value) => sum + value, 0),
        ),
    [revenue],
  );

  const totals = revenue?.totals;
  const trend = totals?.previous
    ? ((totals.current - totals.previous) / totals.previous) * 100
    : null;
  const labels = REVENUE_PERIOD_LABELS[period];

  const activeSlots = revenue?.points.filter((point) =>
    Object.values(point.values).some((value) => value > 0),
  ).length;
  const average = totals
    ? Math.round(totals.current / (activeSlots || 1))
    : undefined;
  const leader = branchTotals[0];
  const leaderShare =
    leader && totals?.current
      ? Math.round((leader.value / totals.current) * 100)
      : 0;
  const todayShare = totals?.current
    ? Math.round((totals.today / totals.current) * 100)
    : null;
  const yesterdayShare = totals?.current
    ? Math.round((totals.yesterday / totals.current) * 100)
    : null;

  const branchOptions = [
    { value: ALL_BRANCHES, label: "Tüm Şubeler" },
    ...(branches ?? []).map((item) => ({
      value: String(item.id),
      label: item.name,
    })),
  ];

  const activeCount = products
    ? products.filter((product) => product.isActive).length
    : undefined;

  const catalogCards = [
    {
      label: "Toplam Ürün",
      value: products?.length,
      icon: IconBox,
      color: "chocolate",
    },
    {
      label: "Aktif Ürün",
      value: activeCount,
      icon: IconCircleCheck,
      color: "teal",
    },
    {
      label: "Kategori",
      value: categories?.length,
      icon: IconCategory,
      color: "grape",
    },
  ];

  const canGoBack = Boolean(
    revenue?.earliest &&
    revenue?.range &&
    revenue.range.from > revenue.earliest,
  );
  const noRevenueEver = Boolean(revenue) && revenue.earliest === null;

  const livePeriod = offset === 0 && period !== "custom";
  const periodName = livePeriod
    ? labels.current
    : formatPeriodRange(period, revenue?.range);
  const slotUnit = revenue?.grain === "month" ? "ay" : "gün";

  const singleBranch =
    branchId !== ALL_BRANCHES ||
    (branches !== undefined && branches.length <= 1);
  const statCardCount = livePeriod ? 4 : 2;
  const RevenueChart = singleBranch ? AreaChart : LineChart;

  return (
    <Stack gap="xl">
      <PageTitle>Genel Bakış</PageTitle>
      <Group>
        <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
          <IconLayoutDashboard size={26} stroke={1.6} />
        </ThemeIcon>
        <Title order={2}>Genel Bakış</Title>
      </Group>

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="revenue" leftSection={<IconCoin size={16} />}>
            Ciro
          </Tabs.Tab>
          <Tabs.Tab value="sales" leftSection={<IconShoppingCart size={16} />}>
            Ürün Satışları
          </Tabs.Tab>
          <Tabs.Tab value="catalog" leftSection={<IconBox size={16} />}>
            Katalog
          </Tabs.Tab>
        </Tabs.List>

        <ViewTransition transitionKey={tab}>
          <Tabs.Panel value="revenue">
            <Stack gap="lg">
              <Group justify="space-between" gap="sm" wrap="wrap">
                {period === "custom" ? (
                  <DatePickerInput
                    aria-label="Tarih aralığı"
                    type="range"
                    value={customRange}
                    onChange={setCustomRange}
                    placeholder="Tarih aralığı seçin"
                    valueFormat="D MMM YYYY"
                    size="sm"
                    w={280}
                    clearable
                  />
                ) : (
                  <Group gap={4} wrap="nowrap">
                    <ActionIcon
                      variant="default"
                      size="lg"
                      onClick={() => setOffset((value) => value - 1)}
                      disabled={!canGoBack}
                      title="Önceki dönem"
                    >
                      <IconChevronLeft size={18} />
                    </ActionIcon>
                    <Text fw={600} ta="center" w={170}>
                      {formatPeriodRange(period, revenue?.range)}
                    </Text>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      onClick={() =>
                        setOffset((value) => Math.min(0, value + 1))
                      }
                      disabled={offset === 0}
                      title="Sonraki dönem"
                    >
                      <IconChevronRight size={18} />
                    </ActionIcon>
                  </Group>
                )}
                <Group gap="sm" wrap="wrap">
                  <SegmentedControl
                    value={period}
                    onChange={(value) => {
                      setPeriod(value);
                      setOffset(0);
                    }}
                    data={REVENUE_PERIODS}
                    size="sm"
                  />
                  {(branches?.length ?? 0) > 1 && (
                    <Select
                      aria-label="Şubeye göre filtrele"
                      value={branchId}
                      onChange={(value) => setBranchId(value ?? ALL_BRANCHES)}
                      data={branchOptions}
                      allowDeselect={false}
                      w={190}
                      size="sm"
                    />
                  )}
                  <SyncButton
                    mutation={syncMutation}
                    cooldown={syncCooldown}
                    total={REVENUE_SYNC_COOLDOWN_SECONDS}
                  />
                </Group>
              </Group>

              {noRevenueEver && !isError && (
                <Alert
                  color="gray"
                  variant="light"
                  icon={<IconPlugConnected size={18} />}
                  title="Henüz ciro verisi yok"
                >
                  <Group
                    justify="space-between"
                    align="center"
                    wrap="wrap"
                    gap="sm"
                  >
                    <Text size="sm">
                      Ciro, şubelere bağlı POS sisteminden saat başı çekiliyor.
                    </Text>
                    {can("connections") && (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => navigate("/connections")}
                      >
                        Bağlantılar
                      </Button>
                    )}
                  </Group>
                </Alert>
              )}

              {isError && (
                <Alert
                  color="red"
                  variant="light"
                  icon={<IconAlertTriangle size={18} />}
                  title="Ciro verisi alınamadı"
                >
                  <Group
                    justify="space-between"
                    align="center"
                    wrap="wrap"
                    gap="sm"
                  >
                    <Text size="sm">Sunucu yanıt vermedi.</Text>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => refetch()}
                    >
                      Tekrar dene
                    </Button>
                  </Group>
                </Alert>
              )}

              <SimpleGrid
                cols={{ base: 1, sm: 2, lg: 3, xl: statCardCount }}
                spacing="lg"
              >
                {livePeriod && (
                  <StatCard
                    label="Bugün"
                    value={totals ? formatPrice(totals.today) : undefined}
                    icon={IconCoin}
                    color="chocolate"
                    hint={
                      todayShare === null
                        ? undefined
                        : `${labels.current.toLowerCase()} cirosunun %${todayShare}'i`
                    }
                  />
                )}
                {livePeriod && (
                  <StatCard
                    label="Dün"
                    value={totals ? formatPrice(totals.yesterday) : undefined}
                    icon={IconCoin}
                    color="chocolate"
                    hint={
                      yesterdayShare === null
                        ? undefined
                        : `${labels.current.toLowerCase()} cirosunun %${yesterdayShare}'i`
                    }
                  />
                )}
                <StatCard
                  label={`${periodName} — Ciro`}
                  value={totals ? formatPrice(totals.current) : undefined}
                  icon={IconCoin}
                  color="chocolate"
                  trend={trend}
                  hint={labels.previous}
                  spark={sparkData}
                />
                <StatCard
                  label={`Ortalama / ${slotUnit}`}
                  value={
                    average === undefined ? undefined : formatPrice(average)
                  }
                  icon={IconCoin}
                  color="chocolate"
                  hint={
                    activeSlots ? `${activeSlots} ${slotUnit} veri` : undefined
                  }
                />
              </SimpleGrid>

              <ViewTransition transitionKey={`${branchId}-${period}-${offset}`}>
                <SimpleGrid
                  cols={{ base: 1, lg: singleBranch ? 1 : 3 }}
                  spacing="lg"
                >
                  <Card
                    withBorder
                    radius="md"
                    padding="lg"
                    style={{ gridColumn: singleBranch ? undefined : "span 2" }}
                  >
                    <Group justify="space-between" mb="md">
                      <Text fw={700}>
                        {singleBranch ? "Ciro" : "Ciro — Şube Kırılımı"}
                      </Text>
                      {isFetching && <Loader size="xs" />}
                    </Group>
                    {isError ? (
                      <ListEmpty>Veri yok</ListEmpty>
                    ) : isPending ? (
                      <Loader size="sm" />
                    ) : chartData.length ? (
                      <RevenueChart
                        h={280}
                        data={chartData}
                        dataKey="label"
                        series={series}
                        curveType="monotone"
                        withLegend={!singleBranch}
                        withDots={false}
                        valueFormatter={formatPrice}
                        yAxisProps={{
                          tickFormatter: formatPriceCompact,
                          width: 58,
                        }}
                        {...(singleBranch ? { withGradient: true } : {})}
                      />
                    ) : (
                      <ListEmpty>Bu dönem için veri yok</ListEmpty>
                    )}
                  </Card>

                  {!singleBranch && (
                    <Card withBorder radius="md" padding="lg">
                      <Text fw={700}>Şube Sıralaması</Text>
                      <Text c="dimmed" size="sm" mb="md">
                        {leader
                          ? `${leader.name} önde · ${formatPrice(leader.value)} · toplamın %${leaderShare}'i`
                          : "Bu dönem için veri yok"}
                      </Text>
                      {isError ? (
                        <ListEmpty>Veri yok</ListEmpty>
                      ) : isPending ? (
                        <Loader size="sm" />
                      ) : branchTotals.length ? (
                        <BarsList
                          data={branchTotals}
                          valueFormatter={formatPrice}
                          barsLabel="Şube"
                          valueLabel="Ciro"
                        />
                      ) : (
                        <ListEmpty>Veri yok</ListEmpty>
                      )}
                    </Card>
                  )}
                </SimpleGrid>
              </ViewTransition>
            </Stack>
          </Tabs.Panel>

          {/* Ciro "ne kadar", katalog "neyi satıyoruz", bu sekme "hangisinden kaç
                        adet gitti" sorusunu cevaplıyor. Ciro'nun hemen ardında, çünkü aynı
                        veriden besleniyor ve çoğu zaman ciroya bakan kişi arkasından bunu
                        soruyor. */}
          <Tabs.Panel value="sales">
            <ProductSales
              branches={branches}
              sync={syncMutation}
              cooldown={syncCooldown}
              cooldownTotal={REVENUE_SYNC_COOLDOWN_SECONDS}
            />
          </Tabs.Panel>

          <Tabs.Panel value="catalog">
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
              {catalogCards.map((stat) => (
                <StatCard key={stat.label} {...stat} />
              ))}
            </SimpleGrid>
          </Tabs.Panel>
        </ViewTransition>
      </Tabs>
    </Stack>
  );
}
